import { Cancel, Complete, Edit, Options } from '@morph-mapper/ui';
import { Checkbox, Group, Menu, Tooltip, useMantineTheme } from '@mantine/core';
import { AiSuggestions } from '../../../common/features/variable-row/molecules/ai-suggestions';
import { StyledActionCell } from './styles';
import { useItemEntryContext, useInlineEntryContext } from '../../context';
import { LuClipboardPaste, LuCopy, LuEraser } from 'react-icons/lu';
import { TypeChangeMenuSection } from '../type-change-menu-section';
import { useStore } from '@/store';
import { useNotify } from '@/hooks';

const CopyPaste = () => {
  const copyEntryToClipboard = useStore(
    ({ entries: e }) => e.copyEntryToClipboard,
  );
  const pasteEntryFromClipboard = useStore(
    ({ entries: e }) => e.pasteEntryFromClipboard,
  );
  const [entryId, hasValue] = useItemEntryContext((s) => [
    s.getId(),
    s.hasValue(),
  ]);

  const theme = useMantineTheme();
  const notify = useNotify();

  const handleCopy = async () => {
    try {
      await copyEntryToClipboard(entryId);
      notify.success('Copied — paste into any open template.');
    } catch {
      notify.error('Copy failed.');
    }
  };

  const handlePaste = async () => {
    try {
      const ok = await pasteEntryFromClipboard(entryId);
      if (!ok) notify.warn('No MorphMapper field found on clipboard.');
    } catch (e) {
      const msg = e instanceof Error ? e.message : '';
      if (msg === 'permission-denied') {
        notify.warn(
          'Browser denied clipboard access — check site permissions.',
        );
      } else {
        notify.error('Paste failed.');
      }
    }
  };

  return (
    <>
      {hasValue && (
        <Menu.Item
          icon={<LuCopy color={theme.colors.gray[6]} />}
          onClick={handleCopy}
        >
          Copy
        </Menu.Item>
      )}
      <Menu.Item
        icon={<LuClipboardPaste color={theme.colors.gray[6]} />}
        onClick={handlePaste}
      >
        Paste
      </Menu.Item>
    </>
  );
};

const EditActions = () => {
  const [hasValue, onEdit, onClear, name, updateEnabled, setUpdateEnabled] =
    useItemEntryContext((s) => [
      s.hasValue(),
      s.onEdit,
      s.onClear,
      s.getName(),
      s.getUpdateEnabled(),
      s.setUpdateEnabled,
    ]);
  const isUpdateDiff = useStore(({ config }) => config.isUpdateDiff);
  const theme = useMantineTheme();
  const label = name ?? 'entry';

  return (
    <StyledActionCell>
      <Group>
        {isUpdateDiff && (
          <Tooltip label="Enable/Disable Rule">
            <Checkbox
              checked={updateEnabled}
              onChange={(event) =>
                setUpdateEnabled(event.currentTarget.checked)
              }
            />
          </Tooltip>
        )}
        <Edit
          aria-label={`Edit ${label}`}
          data-testid={`entry-edit-${label}`}
          onClick={onEdit}
        />
        <AiSuggestions />
        <Menu shadow="md" width={150}>
          <Menu.Target>
            <Options aria-label={`More actions for ${label}`} />
          </Menu.Target>
          <Menu.Dropdown>
            <TypeChangeMenuSection />
            <Menu.Label>Actions</Menu.Label>
            <CopyPaste />
            <Menu.Item
              icon={<LuEraser color={theme.colors.gray[6]} />}
              onClick={onClear}
              disabled={!hasValue}
              data-testid={`entry-clear-${label}`}
            >
              Clear
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </StyledActionCell>
  );
};

const ConfirmActions = () => {
  const [confirmEdit, cancelEdit, name] = useInlineEntryContext((s) => [
    s.confirmEdit,
    s.cancelEdit,
    s.getName(),
  ]);
  const label = name ?? 'entry';

  return (
    <StyledActionCell>
      <Group>
        <Complete
          aria-label={`Confirm ${label}`}
          data-testid={`entry-confirm-${label}`}
          onClick={confirmEdit}
        />
        <Cancel
          aria-label={`Cancel ${label}`}
          data-testid={`entry-cancel-${label}`}
          onClick={cancelEdit}
        />
      </Group>
    </StyledActionCell>
  );
};

export const ActionCell = () => {
  const [isEditing] = useItemEntryContext((s) => [s.isEditing]);

  return (
    <StyledActionCell>
      <Group>{isEditing ? <ConfirmActions /> : <EditActions />}</Group>
    </StyledActionCell>
  );
};
